import {
  CHANNEL_CONFIG,
  DEFAULT_CHANNEL_CONFIG,
  EQUIPMENT_GROUPS,
  FUEL_OPTIONS,
} from "services/api/sales/listings/consts";
import { ListingEditFormValues } from "ui/modules/sales/pages/listings/components/ListingEditCard/types";

import {
  LISTING_INTRO_DEFAULT_IMAGE,
  LISTING_INTRO_MAX_CHANNELS,
  LISTING_INTRO_SAMPLE,
  LISTING_INTRO_SAMPLE_CHANNELS,
} from "./consts";
import { ListingIntroChannel } from "./types";

/** Proizvođač i model vozila, ili primjer dok vozilo nije učitano. */
export const getVehicleName = (formValues: ListingEditFormValues) =>
  [formValues.vehicle?.vehicleManufacturer?.name, formValues.vehicle?.vehicleModel?.name].filter(Boolean).join(" ") ||
  formValues.vehicle?.name ||
  LISTING_INTRO_SAMPLE.vehicleName;

/** Naslov oglasa, a dok nije upisan, naziv vozila. */
export const getListingTitle = (formValues: ListingEditFormValues) => formValues.baseTitle.trim() || getVehicleName(formValues);

export const getVehicleImage = (formValues: ListingEditFormValues) =>
  formValues.vehicle?.defaultImage || LISTING_INTRO_DEFAULT_IMAGE;

/** Odabrane fotografije oglasa, redom kojim su odabrane. */
export const getSelectedPictureUrls = (formValues: ListingEditFormValues) =>
  formValues.selectedPictureUploadIds
    .map((id) => formValues.pictures.find((picture) => picture.id === id)?.url)
    .filter((url): url is string => !!url);

/** Odabrani oglasnici po redoslijedu kanala, ili primjeri dok nijedan nije odabran. */
export const getSelectedChannels = (formValues: ListingEditFormValues): ListingIntroChannel[] => {
  const selected = formValues.channels
    .filter((channel) => formValues.selectedChannelIds.includes(channel.id))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((channel) => ({ id: channel.id, code: channel.code, name: channel.name }));

  return (selected.length > 0 ? selected : LISTING_INTRO_SAMPLE_CHANNELS).slice(0, LISTING_INTRO_MAX_CHANNELS);
};

export const getChannelBadge = (code: string) => (CHANNEL_CONFIG[code] ?? DEFAULT_CHANNEL_CONFIG).badge;

export const getBasePrice = (formValues: ListingEditFormValues) =>
  Number(formValues.basePrice) || LISTING_INTRO_SAMPLE.price;

/** Cijena na oglasniku: prilagođena ako postoji, inače osnovna cijena oglasa. */
export const getChannelPrice = (formValues: ListingEditFormValues, channelId: string) =>
  Number(formValues.channelData[channelId]?.priceOverride) || getBasePrice(formValues);

/** Godina, kilometraža i gorivo u jednom retku, kao na oglasniku. */
export const getSpecsLine = (formValues: ListingEditFormValues) => {
  const { specs } = formValues.marketplaceData;
  const { vehicle } = formValues;
  const year =
    Number(specs.manufacturingYear) ||
    (vehicle?.firstRegistrationDate ? new Date(vehicle.firstRegistrationDate).getFullYear() : null);
  const km = Number(specs.kmTraveled) || vehicle?.kmTraveled;
  const fuel = FUEL_OPTIONS.find((option) => option.value === specs.fuelTypeCode)?.text;
  const parts = [year ? String(year) : null, km ? `${km.toLocaleString("hr-HR")} km` : null, fuel].filter(Boolean);

  return parts.length > 0 ? parts.join(" • ") : LISTING_INTRO_SAMPLE.specs;
};

/** Nazivi označene opreme, ili primjer dok ništa nije označeno. */
export const getEquipmentLabels = (formValues: ListingEditFormValues, count: number) => {
  const selected = EQUIPMENT_GROUPS.flatMap((group) => group.items)
    .filter((item) => formValues.marketplaceData.equipment.selected[item.code] === true)
    .map((item) => item.label);

  return (selected.length > 0 ? selected : LISTING_INTRO_SAMPLE.equipment).slice(0, count);
};
